import { Inject } from '@odinjs/odin';
import { ActionAPI, AutocompleteAPI, ButtonAPI, FieldAPI, FormAPI, InputAPI, ModalProps, ModalService, PanelAPI, TextAreaAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeCancel, OnBeforeSubmit, OnChange, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';

import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import ShowToast from '../../../utils/toast-service';

import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { Endpoint } from '../../../constants/endpoints';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { SimpleDialog } from '../../dialog-helper';
import ClinicalNotesService, { EventListenerController } from '../clinical-note-service';

import './clinical-notes-editor-controller.css';
import { ClinicalNotesEditorAliases } from '../../../codegen/constants/aliases/clinical-notes-editor-aliases';
import { YesNoType } from '../../../utils/enum-utils';
import { FragmentNames } from '../../../codegen';
import { FunctionName, PEP_281Parameters } from '../../../utils/parameters';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { OpenConsentModalService } from '../clinical-notes-consent/open-consent-modal-service';

interface Especialidade {
  cdEspecialidade: number;
  dsEspecialidade: string;
  ieParecer: string;
  nrSeqApres: number;
}

interface MedicoParecer {
  cdPessoaFisica: string;
  nmPessoaFisica: string;
}

interface TipoNotaClinica {
  cdTipoEvolucao: string;
  dsCor: string;
  dsTipoEvolucao: string;
  ieEspecParecer: string;
  ieObrigarPessoaParecer: string;
  nrSeqTexto: number;
}

interface NotaClinicaDTO {
  cdEvolucao: number;
  cdPessoaFisica: string;
  cdSetorAtendimento: number;
  dsEvolucao: string;
  dtAtualizacaoNrec: string;
  dtEvolucao: string;
  especialidadeMedica: Especialidade | undefined;
  medicoParecer: MedicoParecer | undefined;
  nrAtendimento: number;
  tipoNotaClinica: TipoNotaClinica | undefined;
}

interface TimeValue {
  currentDateTime: string;
}

interface PersonAutoCompleteProps {
  cdPessoaFisica: number;
  nmPessoaFisica: string;
}

@Controller({ component: ClinicalNotesEditorAliases.LAYOUT_CLINICAL_NOTES_EDITOR, fragment: FragmentNames.CLINICAL_NOTES_EDITOR })
class ClinicalNotesEditor extends BaseController {

  @Inject(ClinicalNotesEditorAliases.CLINICAL_NOTES_PANEL_SUCCESS_LOADER)
  private loaderSuccerssPanelApi!: PanelAPI;

  @Inject(ClinicalNotesEditorAliases.AUTOC_CLINICAL_NOTES_TYPE)
  private autocClinicalNotesType!: AutocompleteAPI;

  @Inject(ClinicalNotesEditorAliases.FIELD_MEDICO_PARECER)
  private fieldMedicoParecer!: FieldAPI;

  @Inject(ClinicalNotesEditorAliases.FIELD_MEDICAL_SPECIALTY)
  private fieldMedicalSpecialty!: FieldAPI;

  @Inject(ClinicalNotesEditorAliases.TEXT_AREA_EVOLUTION_DESC)
  private textEvolution!: TextAreaAPI;

  @Inject(ClinicalNotesEditorAliases.FORM_CLINICAL_NOTES)
  private formClinicalNotes!: FormAPI;

  @Inject(ClinicalNotesEditorAliases.NEW_CLINICAL_NOTE_ACTION)
  private newClinicalNoteAction!: ActionAPI;

  @Inject(ClinicalNotesEditorAliases.DELETE_CLINICAL_NOTE_ACTION)
  private deleteClinicalNoteAction!: ActionAPI;

  @Inject(ClinicalNotesEditorAliases.INACTIVATE_CLINICAL_NOTE_ACTION)
  private inactivateClinicalNoteAction!: ActionAPI;

  @Inject(ClinicalNotesEditorAliases.ADD_MY_STANDARD_TEXT_ACTION)
  private addMyStandardTextAction!: ActionAPI;

  @Inject(ClinicalNotesEditorAliases.ADD_INSTITUTION_STANDARD_TEXT_ACTION)
  private addInstitutionStandardTextAction!: ActionAPI;

  @Inject(ClinicalNotesEditorAliases.PANEL_LOAD_RELEASE)
  private panelLoaderRelease!: PanelAPI;

  @Inject(ClinicalNotesEditorAliases.RELEASE_BUTTON)
  private buttonReleaseApi!: ButtonAPI;

  editSubmit = false;
  today!: string;

  notaClinica!: NotaClinicaDTO;
  cnService!: ClinicalNotesService;

  releaseInProcess!: boolean;

  onMountDefaultEvolutionType!: boolean;

  configureMenu(): void {
    const { cdEvolucao, encounterId, eStatus, patientId } = this.historyService.getCurrentRouteParams();
    const status = 'P';

    this.context.set({ encounterId, eStatus, evolutionId: cdEvolucao, patientId });
    this.inactivateClinicalNoteAction.setEnabled(false);

    if (eStatus === status) {
      this.deleteClinicalNoteAction.setEnabled(true);
      this.addMyStandardTextAction.setEnabled(true);
      this.addInstitutionStandardTextAction.setEnabled(true);
    } else {
      this.newClinicalNoteAction.setEnabled(false);
      this.deleteClinicalNoteAction.setEnabled(false);
      this.addMyStandardTextAction.setEnabled(true);
      this.addInstitutionStandardTextAction.setEnabled(true);
    }
  }

  @OnMount({ target: ClinicalNotesEditorAliases.NEW_CLINICAL_NOTE_ACTION })
  handleNewClinicalNoteActionMount(): void {
    this.configureMenu();
  }

  @OnMount({ target: ClinicalNotesEditorAliases.DELETE_CLINICAL_NOTE_ACTION })
  handleDeleteClinicalNoteActionMount(): void {
    this.configureMenu();
  }

  @OnMount({ target: ClinicalNotesEditorAliases.INACTIVATE_CLINICAL_NOTE_ACTION })
  handleInvactivateClinicalNoteActionMount(): void {
    this.configureMenu();
  }

  @OnMount({ target: ClinicalNotesEditorAliases.ADD_MY_STANDARD_TEXT_ACTION })
  handleAddMyStandardTextActionMount(): void {
    this.configureMenu();
  }

  @OnMount({ target: ClinicalNotesEditorAliases.ADD_INSTITUTION_STANDARD_TEXT_ACTION })
  handleAddInstitutionStandardTextActionMount(): void {
    this.configureMenu();
  }

  @OnClick({ target: ClinicalNotesEditorAliases.DELETE_CLINICAL_NOTE_ACTION })
  handleDelete(): void {
    const evolutionId = this.context.get('evolutionId');
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');
    const cnService = new ClinicalNotesService(this);

    cnService.confirmDelete(patientId, encounterId, parseInt(evolutionId));
  }

  @OnClick({ target: ClinicalNotesEditorAliases.INACTIVATE_CLINICAL_NOTE_ACTION })
  handleInactivate(): void {
    const evolutionId = this.context.get('evolutionId');
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'patient-evolution-inactivation',
      id: 'patient-evolution-inactivation',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      encounterId,
      evolutionId,
      patientId,
    }

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ClinicalNotesEditorAliases.NEW_CLINICAL_NOTE_ACTION })
  handleNew(): void {
    OpenConsentModalService.openConsentOptionsModal()
  }

  @OnClick({ target: ClinicalNotesEditorAliases.ADD_MY_STANDARD_TEXT_ACTION })
  handleMyStandardTextClick(): void {
    const clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { clinicalNoteTypeId },
      focusOnOpen: false,
      fragment: 'clinical-notes-my-standard-text',
      id: 'clinical-notes-my-standard-text',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'form-title.clinical-notes-edit.standard-text',
      uuid: generateUUID(),
    };

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ClinicalNotesEditorAliases.ADD_INSTITUTION_STANDARD_TEXT_ACTION })
  handleInstStandardTextClick(): void {
    const clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { clinicalNoteTypeId },
      focusOnOpen: false,
      fragment: 'clinical-notes-institution-standard-text',
      id: 'clinical-notes-institution-standard-text',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'form-title.clinical-notes-edit.standard-text',
      uuid: generateUUID(),
    };

    ModalService.openDefault(modalProperties);
  }

  loadClincalNote(evolutionId: number): void {
    const patientId = this.context.get('patientId');
    const encounterId = this.context.get('encounterId');
    this.formClinicalNotes.setLoading(true)
    HttpService.get(
      Endpoint.CLINICAL_NOTE,
      {
        cdEvolucao: evolutionId,
        cdPessoaFisica: patientId,
        nrAtendimento: encounterId,
      }
    ).then(response => {
      this.formClinicalNotes.setEditing(true);
      this.formClinicalNotes.setDisabled(false);
      const clinicalNote = response.body as NotaClinicaDTO;

      this.formClinicalNotes.setData(clinicalNote as unknown as Record<string, NotaClinicaDTO>);
      const evolutionDate = moment(clinicalNote.dtEvolucao);
      this.context.set({ evolutionDate });
      this.buttonReleaseApi.setVisible(true);

      const clinicalNoteType = clinicalNote.tipoNotaClinica as TipoNotaClinica;
      this.showFieldsOpinion(clinicalNoteType);
    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => this.formClinicalNotes.setLoading(false));
  }

  getValueSpecialty(): Value {
    return this.fieldMedicalSpecialty.getValue();
  }

  showFieldsOpinion(tipoNotaClinica: TipoNotaClinica | undefined): void {

    if (tipoNotaClinica) {

      const ieEspecParecer = tipoNotaClinica.ieEspecParecer;
      const ieObrigarPessoaParecer = tipoNotaClinica.ieObrigarPessoaParecer;
      const visible = ieEspecParecer && ieEspecParecer === YesNoType.YES ? true : false;
      const required = ieObrigarPessoaParecer && ieObrigarPessoaParecer === YesNoType.YES ? true : false;

      this.fieldMedicoParecer.setVisible(visible);
      this.fieldMedicalSpecialty.setVisible(visible);

      if (!visible) {

        this.fieldMedicoParecer.setValue(undefined);
        this.fieldMedicalSpecialty.setValue(undefined);

      } else if (required) {

        this.fieldMedicoParecer.removeValidator('required');
        const validator: Validator = { name: 'required', options: {} };
        this.fieldMedicoParecer.addValidator(validator);
        this.fieldMedicoParecer.validate();

      }

    } else {

      this.fieldMedicoParecer.removeValidator('required');
      this.fieldMedicoParecer.setVisible(false);
      this.fieldMedicalSpecialty.setVisible(false);

    }
  }

  async createClinicalNote(notaClinicaDTO: NotaClinicaDTO): Promise<boolean> {
    const body = {
      cdEvolucao: notaClinicaDTO.cdEvolucao,
      cdPessoaFisica: notaClinicaDTO.cdPessoaFisica,
      cdSetorAtendimento: notaClinicaDTO.cdSetorAtendimento,
      dsEvolucao: notaClinicaDTO.dsEvolucao,
      dtAtualizacaoNrec: notaClinicaDTO.dtAtualizacaoNrec,
      dtEvolucao: moment(String(notaClinicaDTO.dtEvolucao)).utc().format(ISO_FULL_DATE_FORMAT),
      especialidadeMedica: notaClinicaDTO.especialidadeMedica,
      medicoParecer: notaClinicaDTO.medicoParecer,
      nrAtendimento: notaClinicaDTO.nrAtendimento,
      tipoNotaClinica: notaClinicaDTO.tipoNotaClinica,
    }

    const patientId = this.context.get('patientId');
    const encounterId = this.context.get('encounterId');
    const canSubmit: Promise<boolean> = HttpService.post(Endpoint.POST_CLINICAL_NOTE, {
      cdPessoaFisica: patientId,
      nrAtendimento: encounterId
    }, { body }).then(response => {
      const evolutionId = response.body as number;

      if (evolutionId && evolutionId > 0) {
        this.context.set({ evolutionId });
        this.loaderSuccerssPanelApi.setVisible(false);
        return false;
      }
      return true;

    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
      this.loaderSuccerssPanelApi.setVisible(false);

      return true;
    });

    return canSubmit;
  }

  updateClinicalNote(notaClinicaDTO: NotaClinicaDTO): Promise<boolean> {
    const body = {
      cdEvolucao: notaClinicaDTO.cdEvolucao,
      cdPessoaFisica: notaClinicaDTO.cdPessoaFisica,
      cdSetorAtendimento: notaClinicaDTO.cdSetorAtendimento,
      dsEvolucao: notaClinicaDTO.dsEvolucao,
      dtEvolucao: notaClinicaDTO.dtEvolucao,
      especialidadeMedica: notaClinicaDTO.especialidadeMedica,
      medicoParecer: notaClinicaDTO.medicoParecer,
      nrAtendimento: notaClinicaDTO.nrAtendimento,
      tipoNotaClinica: notaClinicaDTO.tipoNotaClinica,
    }

    const { cdEvolucao } = body;
    const patientId = this.context.get('patientId');
    const encounterId = this.context.get('encounterId');
    const canSubmit: Promise<boolean> = HttpService.put(Endpoint.CLINICAL_NOTE, {
      cdEvolucao,
      cdPessoaFisica: patientId,
      nrAtendimento: encounterId,
    }, { body }).then(response => {

      if (response.status === 200 && response.body as number > 0) {
        const evolutionId = response.body as number;
        this.context.set({ evolutionId });
        this.loaderSuccerssPanelApi.setVisible(false);
        return false;
      }
      SimpleDialog.showWarningMessage(`${response.status} - ${response.statusText}`);

      return true;

    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
      this.loaderSuccerssPanelApi.setVisible(false);

      return true;
    });

    return canSubmit;
  }

  removeStandardTextEvent(): void {
    if (EventListenerController.addStandardText) {
      removeEventListener('addStandardText', EventListenerController.addStandardText);
      EventListenerController.addStandardText = undefined;
    }
  }

  listenerAddStandardTExt = (event: Event): void => {
    const customEvent: CustomEvent = event as CustomEvent;
    const dsStandardText = customEvent.detail as string;
    let text = this.textEvolution.getValue();

    if (text && text?.length > 0) {
      text = dsStandardText + '\n\n' + text;
    } else {
      text = dsStandardText;
    }

    this.textEvolution.setValue(text);
  }

  addStandardTextEvent(): void {
    if (!EventListenerController.addStandardText) {
      EventListenerController.addStandardText = this.listenerAddStandardTExt;
      addEventListener('addStandardText', this.listenerAddStandardTExt);
    }
  }

  getStandardTextByCNoteType(cdTipoEvolucao: string): Promise<number> {

    const nrTextoPadrao: Promise<number> = HttpService.get(

      Endpoint.GET_NOTAS_CLINICAS_TYPE_BY_ID,
      { cdTipoEvolucao }

    ).then(response => {

      const tipoNotaClinica = response.body as TipoNotaClinica;

      if (tipoNotaClinica && tipoNotaClinica.nrSeqTexto) {
        return tipoNotaClinica.nrSeqTexto;
      }

      return 0;

    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
      return 0;
    });

    return nrTextoPadrao;
  }

  isMedicalOpinionRequired(): boolean {
    const tipoNotaClinica = this.autocClinicalNotesType.getValue() as Record<string, Value>;
    return tipoNotaClinica && tipoNotaClinica.ieEspecParecer && tipoNotaClinica.ieObrigarPessoaParecer === YesNoType.YES ? true : false;
  }

  @OnMount({ target: ClinicalNotesEditorAliases.LAYOUT_CLINICAL_NOTES_EDITOR })
  handleMount(): void {

    InformationBarUtils.collapsedBar = true;
    this.releaseInProcess            = false;
    this.onMountDefaultEvolutionType = true;
    this.editSubmit                  = false;
    this.cnService = new ClinicalNotesService(this);
    const { patientId, isEdit, cdEvolucao, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      cdPessoaFisica: patientId,
      clinicalNoteTypeId: '',
      encounterId,
      evolutionId: cdEvolucao,
      nrAtendimento: encounterId,
      patientId,
    })

    if (isEdit === YesNoType.YES) {
      this.loadClincalNote(Number(cdEvolucao));
    }

    this.addStandardTextEvent();
  }

  @OnMount({ target: ClinicalNotesEditorAliases.RELEASE_BUTTON })
  handleReleaseButtonMount(): void {
    const { isEdit } = this.historyService.getCurrentRouteParams();
    this.buttonReleaseApi.setVisible(isEdit === YesNoType.YES);
  }

  @OnMount({ target: ClinicalNotesEditorAliases.FORM_CLINICAL_NOTES })
  handleFormMount(): void {
    const { isEdit } = this.historyService.getCurrentRouteParams();

    if (isEdit === YesNoType.YES) {
      this.formClinicalNotes.setEditing(false);
    } else {
      this.showFieldsOpinion(undefined);
      this.formClinicalNotes.setLoading(true);
      this.getCurrentDate()
        .then(response => {
          if (!response.body?.currentDateTime) {
            Promise.reject();
          }
          this.defineDatesInForm(moment(response.body?.currentDateTime));
        })
        .catch(() => this.defineDatesInForm(moment()))
        .finally(() => {
          this.formClinicalNotes.setLoading(false);
        })
    }
  }

  defineDatesInForm(currentDate: moment.Moment): void {
    this.today = currentDate.format(ISO_FULL_DATE_FORMAT);
    this.formClinicalNotes.setData({ dtEvolucao: currentDate.utc().format(ISO_FULL_DATE_FORMAT) });
    this.context.set({ evolutionDate: currentDate })
  }

  getCurrentDate(): Promise<HttpResponse<TimeValue>> {
    return HttpService.get<TimeValue>(Endpoint.GET_CURRENT_DATE_TIME)
  }

  @OnUnmount({ target: ClinicalNotesEditorAliases.LAYOUT_CLINICAL_NOTES_EDITOR })
  handleUnmount(): void {
    this.removeStandardTextEvent();
  }

  @OnAfterFetch({ target: ClinicalNotesEditorAliases.FETCH_CLINICAL_NOTES_TYPE })
  handleAfterFetch(event: FetchEvent): void {
    const { isEdit }            = this.historyService.getCurrentRouteParams();
    const clinicalNotesTypeList = event.data as Record<string, Value>[];
    const setDefaultValue       = clinicalNotesTypeList && this.onMountDefaultEvolutionType;

    if (isEdit !== YesNoType.YES && setDefaultValue) {
      const evolutionTypeDefaultValue  = FoundationUtils.findModuleParam(FunctionName.PEP_281, PEP_281Parameters.DEFAULT_EVOLUTION_TYPE_TO_ADD_NEW_EVOLUTION);
      const clinicalNotesType          = clinicalNotesTypeList.find((item) => item.cdTipoEvolucao === evolutionTypeDefaultValue);
      this.onMountDefaultEvolutionType = false;
      this.autocClinicalNotesType.setValue(clinicalNotesType);
    }
  }

  @OnChange({ target: ClinicalNotesEditorAliases.INPUT_DATETIME })
  handleDataTimeInputChange(evt: ChangeEvent<InputAPI>): void {
    const newDateState = moment(String(evt.value));
    this.context.set({ evolutionDate: newDateState });
    const evolutionId = this.context.get('evolutionId');

    if (evolutionId && evolutionId > 0) {
      this.formClinicalNotes.setDisabled(false);
    }
  }

  @OnChange({ target: [ClinicalNotesEditorAliases.AUTOC_MEDICAL_SPECIALTY, ClinicalNotesEditorAliases.TEXT_AREA_EVOLUTION_DESC] })
  handleChange(): void {
    const evolutionId = this.context.get('evolutionId');
    if (evolutionId && evolutionId > 0) {
      this.formClinicalNotes.setDisabled(false);
    }
  }

  @OnChange({ target: ClinicalNotesEditorAliases.FORM_CLINICAL_NOTES })
  handleClinicalNotesChanges(e: ChangeEvent<NotaClinicaDTO>): void {
    const evolutionId = this.context.get('evolutionId');
    if (this.editSubmit === false) {
      if (evolutionId && evolutionId > 0) {
        this.formClinicalNotes.setDisabled(true);
      } else {
        this.formClinicalNotes.setDisabled(false);
      }
    }

    if (JSON.stringify(e.oldValue) != JSON.stringify(e.value)) {
      this.editSubmit = true;
    }

    this.formClinicalNotes.validate();

  }

  @OnChange({ target: ClinicalNotesEditorAliases.AUTOC_CLINICAL_NOTES_TYPE })
  handleClinicalNoteTypeChange(event: ChangeEvent<TipoNotaClinica>): void {
    const tipoNotaClinica = event.value;
    const evolutionId = this.context.get('evolutionId');
    this.textEvolution.setValue('');

    if (tipoNotaClinica) {

      this.context.set({
        clinicalNoteTypeId: tipoNotaClinica ? tipoNotaClinica.cdTipoEvolucao : '0'
      });

      this.getStandardTextByCNoteType(tipoNotaClinica.cdTipoEvolucao)
        .then(nrTextoPadrao => {

          if (nrTextoPadrao && nrTextoPadrao > 0) {
            const dialogModal: ModalProps = {
              closeable: false,
              closeByDocument: false,
              closeByEscape: false,
              focusOnOpen: false,
              fragment: 'clinical-notes-auto-standard-text',
              id: 'clinical-notes-auto-standard-text',
              onCancel: () => { },
              onSubmit: () => { },
              title: 'form-title.clinical-notes-edit.standard-text',
              uuid: generateUUID(),
            };
            if (evolutionId && evolutionId > 0) {
              this.formClinicalNotes.setDisabled(false);
              const eStatus = this.context.get('eStatus');

              dialogModal.contextData = {
                cdEvolucao: evolutionId,
                eStatus,
                nrTextoPadrao,
              }
              ModalService.openDialog(dialogModal);
            } else {
              dialogModal.contextData = {
                nrTextoPadrao
              }
              ModalService.openDialog(dialogModal);
            }
          } else {
            this.showFieldsOpinion(tipoNotaClinica);
          }
        });

    } else {

      this.context.set({
        clinicalNoteTypeId: ''
      });

      this.showFieldsOpinion(undefined);
    }

  }

  @OnChange(ClinicalNotesEditorAliases.AUTOC_PERSON)
  handleMedicalOpinion(event: ChangeEvent<PersonAutoCompleteProps>): void {
    const personValues = event.value;
    const evolutionId = this.context.get('evolutionId');

    if (evolutionId && evolutionId > 0) {
      this.formClinicalNotes.setDisabled(false);
    }
    if (this.isMedicalOpinionRequired() && !personValues) {
      setTimeout(() => {
        this.formClinicalNotes.validate();
      }, 0);
    } else {
      this.formClinicalNotes.isValid();
    }
  }

  @OnBeforeCancel(ClinicalNotesEditorAliases.FORM_CLINICAL_NOTES)
  handleFormBeforeCancel(event: AbortableSubmitEvent): void {
    event.abort()
    const record = this.formClinicalNotes.getRecord();
    this.formClinicalNotes.setRecord(record);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'clinical-notes.discard-changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard() },
      onClickOk: () => { this.discard() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  @OnClick({ target: ClinicalNotesEditorAliases.RELEASE_BUTTON })
  handleReleaseButtonClick(): void {
    this.handleReleaseClick();
  }

  discard(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  cancelDiscard(): void {
    this.formClinicalNotes.setDisabled(false);
  }

  @OnBeforeSubmit({ target: ClinicalNotesEditorAliases.FORM_CLINICAL_NOTES })
  handleBeforeSubmit(event: AbortableSubmitEvent): void {
    event.abort();
    this.validateSaveClinicalNotes(event)
  }

  async validateSaveClinicalNotes(event: AbortableSubmitEvent): Promise<boolean> {
    this.loaderSuccerssPanelApi.setVisible(true);
    let toPromiseBool: boolean;

    const clinicalNote = event.record as NotaClinicaDTO;
    let evolutionId = this.context.get('evolutionId');
    const evolutionDate = this.context.get('evolutionDate') as string;
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');
    const today = moment();

    if (today.isBefore(evolutionDate)) {

      ToastService.open({
        dismissExpression: MessageService.translate('warning.clinical-note.later-date'),
        dismissible: true,
        highlight: false,
        id: 'administration-by-process',
        level: ToastLevel.WARNING,
        message: MessageService.translate('warning.clinical-note.later-date'),
        placement: Placement.BOTTOM,
        showIcon: true,
        timeout: 5000,
        type: ToastType.BANNER,
      });

      this.loaderSuccerssPanelApi.setVisible(false);

      return true;
    }

    clinicalNote.cdEvolucao = evolutionId;

    if (evolutionId > 0) {
      toPromiseBool = await this.updateClinicalNote(clinicalNote);
    } else {

      clinicalNote.cdEvolucao = 0;
      clinicalNote.cdPessoaFisica = patientId;
      clinicalNote.nrAtendimento = encounterId;
      clinicalNote.cdSetorAtendimento = InformationBarUtils.encounterSectorCode;
      clinicalNote.dtAtualizacaoNrec = this.today;

      toPromiseBool = await this.createClinicalNote(clinicalNote);
      evolutionId = this.context.get('evolutionId');
    }

    this.cnService.confirmCancelAndRelease(patientId, encounterId, evolutionId);

    return toPromiseBool;
  }

  async handleReleaseClick(): Promise<boolean> {
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');
    const evolutionId = this.context.get('evolutionId');

    if (this.releaseInProcess) {
      return false;
    }

    let result = false;
    this.releaseInProcess = true;

    if (this.isMedicalOpinionRequired()) {
      const medicalOpinion = this.fieldMedicoParecer.getValue();
      if (!medicalOpinion) {
        return false;
      }
    }

    try {
      this.panelLoaderRelease.setVisible(true);
      this.formClinicalNotes.setEditing(false);
      this.formClinicalNotes.setDisabled(true);
      this.buttonReleaseApi.setVisible(false);
      await new Promise(r => setTimeout(r, 3000));
      result = await this.cnService.release(patientId, encounterId, evolutionId);
    } catch (error: any) {
      this.panelLoaderRelease.setVisible(false);
      this.buttonReleaseApi.setVisible(true);
      this.formClinicalNotes.setEditing(true);
      this.formClinicalNotes.setDisabled(true);
      SimpleDialog.showErrorMessage(error?.body as string);
      result = false;
    } finally {
      this.releaseInProcess = false;
    }

    return result;
  }
}
export default ClinicalNotesEditor;
